import { useEffect, useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import EnvelopeIntro from "./components/wedding/EnvelopeIntro";
import Footer from "./components/wedding/Footer";
import { SectionDividerFlower } from "./components/wedding/Layout";
import Story from "./components/wedding/sections/Story";
import Countdown from "./components/wedding/sections/Countdown";
import Faith from "./components/wedding/sections/Faith";
import Details from "./components/wedding/sections/Details";
import Travel from "./components/wedding/sections/Travel";
// import Gallery from "./components/wedding/sections/Gallery";
import FAQ from "./components/wedding/sections/FAQ";
import GiftSection from "./components/wedding/sections/Gift";

import RSVP from "./components/wedding/sections/RSVP";
import { scrollToId, useScrollSpy } from "./components/wedding/helpers";
import { I18nProvider, useI18n } from "./components/wedding/i18n";

const SUPPORTED_LANGUAGES = ["en", "de"];

const ENVELOPE_SESSION_KEY = "wedding-envelope-opened";
const LANGUAGE_OPTIONS = [
  { value: "en", flag: "🇬🇧", label: "English" },
  { value: "de", flag: "🇩🇪", label: "Deutsch" },
];

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<LangRedirect />} />
      <Route path="/:lang/*" element={<LangWrapper />} />
    </Routes>
  );
}

function LangRedirect() {
  const preferred = navigator.language?.startsWith("de") ? "de" : "en";
  return <Navigate to={`/${preferred}`} replace />;
}

function LangWrapper() {
  const { lang } = useParams();
  const validLang = SUPPORTED_LANGUAGES.includes(lang) ? lang : "en";
  if (validLang !== lang) return <Navigate to={`/${validLang}`} replace />;
  return (
    <I18nProvider language={validLang}>
      <WeddingApp />
    </I18nProvider>
  );
}

function WeddingApp() {
  const { t, language, ready } = useI18n();
  const navigate = useNavigate();
  const sectionIds = useMemo(
    () => [
      "envelope-hero",
      "story",
      //"details",
      "travel",
      "gallery",
      "faith",
      "faq",
      "rsvp",
    ],
    [],
  );
  const active = useScrollSpy(sectionIds, 140);
  const [opened, setOpened] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    try {
      return window.sessionStorage.getItem(ENVELOPE_SESSION_KEY) === "true";
    } catch {
      return false;
    }
  });
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (!opened) {
      return;
    }

    try {
      window.sessionStorage.setItem(ENVELOPE_SESSION_KEY, "true");
    } catch {
      // Ignore storage failures and keep the UI working.
    }
  }, [opened]);

  if (!ready) {
    return <div className="min-h-screen bg-theme-background" />;
  }

  return (
    <div
      className={`min-h-screen bg-theme-background text-theme-text ${!opened ? "overflow-hidden h-screen" : ""}`}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(800px 400px at 20% 10%, rgba(62,82,60,0.22), transparent 60%)," +
            "radial-gradient(700px 400px at 80% 20%, rgba(176,210,223,0.22), transparent 60%)," +
            "radial-gradient(900px 500px at 50% 100%, rgba(254,254,255,0.32), transparent 55%)",
        }}
      />

      {/* Header appears after envelope is opened */}
      {opened && (
        <motion.header
          initial={{ y: -24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="z-40"
        >
          {/* Glass bar */}
          <div className="mx-auto max-w-6xl px-6">
            <div className="relative m-1">
              <div className="flex items-center justify-between h-14 px-5">
                {/* Left nav items */}
                <nav className="hidden md:flex items-center gap-0.5">
                  {[
                    [t("header.nav.story"), "story"],
                    [t("header.nav.details"), "details"],
                    //[t("header.nav.gallery"), "gallery"],
                  ].map(([label, id]) => (
                    <button
                      key={id}
                      onClick={() => scrollToId(id)}
                      className={`
                      relative px-2.5 py-1.5 text-[13px] tracking-normal rounded-xl transition-all duration-200
                  ${
                    active === id
                      ? "text-theme-text font-medium"
                      : "text-theme-text-subtle hover:text-theme-text-muted"
                  }
                `}
                    >
                      {active === id && (
                        <motion.span
                          layoutId="nav-pill"
                          className="absolute inset-0 rounded-xl bg-theme-border-light"
                          transition={{
                            type: "spring",
                            bounce: 0.2,
                            duration: 0.4,
                          }}
                        />
                      )}
                      <span className="relative">{label}</span>
                    </button>
                  ))}
                </nav>

                {/* Center — S & B monogram */}
                <button
                  onClick={() => scrollToId("envelope-hero")}
                  className="absolute left-1/2 -translate-x-1/2 group flex items-center gap-2"
                >
                  <span className="flex items-baseline gap-1.5">
                    <span className="font-script text-[22px] leading-none tracking-wide text-theme-text group-hover:opacity-70 transition-opacity">
                      {t("app.couple.bride").charAt(0)}
                    </span>
                    <span className="text-[10px] text-theme-text-faint tracking-[0.25em] uppercase font-light select-none pt-0.5">
                      &amp;
                    </span>
                    <span className="font-script text-[22px] leading-none tracking-wide text-theme-text group-hover:opacity-70 transition-opacity">
                      {t("app.couple.groom").charAt(0)}
                    </span>
                  </span>
                </button>

                {/* Right — language + FAQ */}
                <div className="hidden md:flex items-center gap-1">
                  {[
                    [t("header.nav.faith"), "faith"],
                    [t("header.nav.faq"), "faq"],
                  ].map(([label, id]) => (
                    <button
                      key={id}
                      onClick={() => scrollToId(id)}
                      className={`
                      relative px-2.5 py-1.5 text-[13px] tracking-normal rounded-xl transition-all duration-200
                  ${
                    active === id
                      ? "text-theme-text font-medium"
                      : "text-theme-text-subtle hover:text-theme-text-muted"
                  }
                `}
                    >
                      {active === id && (
                        <motion.span
                          layoutId="nav-pill"
                          className="absolute inset-0 rounded-xl bg-theme-border-light"
                          transition={{
                            type: "spring",
                            bounce: 0.2,
                            duration: 0.4,
                          }}
                        />
                      )}
                      <span className="relative">{label}</span>
                    </button>
                  ))}

                  <div className="w-px h-4 bg-theme-border mx-1" />

                  <label className="sr-only" htmlFor="language-select-desktop">
                    {t("app.language.label")}
                  </label>
                  <select
                    id="language-select-desktop"
                    value={language}
                    onChange={(event) => navigate(`/${event.target.value}`)}
                    className="rounded-xl border-0 bg-transparent px-2 py-1.5 text-[13px] text-theme-text-subtle hover:text-theme-text-muted cursor-pointer outline-none transition"
                  >
                    {LANGUAGE_OPTIONS.map(({ value, flag, label }) => (
                      <option key={value} value={value}>
                        {flag} {label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Mobile: compact language select on the right */}
                <div className="absolute right-5 top-1/2 flex -translate-y-1/2 items-center md:hidden">
                  <label className="sr-only" htmlFor="language-select-mobile">
                    {t("app.language.label")}
                  </label>
                  <select
                    id="language-select-mobile"
                    aria-label={t("app.language.label")}
                    value={language}
                    onChange={(event) => navigate(`/${event.target.value}`)}
                    className="w-[3.25rem] rounded-xl border-0 bg-transparent px-2 py-1.5 text-[13px] text-theme-text-subtle outline-none"
                  >
                    {LANGUAGE_OPTIONS.map(({ value, flag }) => (
                      <option key={value} value={value}>
                        {flag}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </motion.header>
      )}

      {/* Envelope Hero Section - First screen */}
      <section
        id="envelope-hero"
        className="relative h-screen w-full overflow-hidden"
      >
        <EnvelopeIntro
          opened={opened}
          onOpen={() => setOpened(true)}
          reducedMotion={prefersReducedMotion}
        />
      </section>

      {/* Main Content - Fades in after envelope opens */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: opened ? 1 : 0 }}
        transition={{ duration: 0.8, delay: opened ? 0.3 : 0 }}
      >
        <Story />
        <div className="mx-auto max-w-6xl p-4">
          <SectionDividerFlower />
          <Countdown />
          <SectionDividerFlower />
          <Details />
          <SectionDividerFlower />
          <Travel />
          <SectionDividerFlower />
          {/* <Gallery /> */}
          {/* <SectionDividerFlower /> */}
          <Faith />
          <SectionDividerFlower />
          <FAQ />
          <SectionDividerFlower />
          <GiftSection />
          <SectionDividerFlower />
          <RSVP />
        </div>
      </motion.div>

      {opened && <Footer />}
    </div>
  );
}
